import React, {useEffect, useState} from "react";
import {Loader, Message, toaster, Progress, Toggle, Whisper} from "rsuite";
import {getChimesModeText, getChimesSchemeText} from "../MainPage.utils";
import StatusControl from "../../../shared/StatusControl/StatusControl";
import UpdateButton from "../../../shared/UpdateButton/UpdateButton";
import GearIcon from '@rsuite/icons/Gear'
import {getTooltip} from "../../../../shared/Tooltip";
import {ClockChimeSettingsApi} from "../../../../services/ClockChimeSettings.api";
import NotificationFactory from "../../../../shared/Notifications";

export interface ChimeSettingsSectionProps {
  deviceId: string;
  onTabChange: any;
  relayStatus: Record<string, string>;
}

const ChimeSettingsSection = ({deviceId, onTabChange, relayStatus}: ChimeSettingsSectionProps) => {
  const [loading, setLoading] = useState(false);
  const [chimesData, setChimesData] = useState<any | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSettingsClick = () => {
    if (onTabChange) {
      onTabChange('chimes');
    }
  };

  const handleRefreshSection = async () => {
    setRefreshLoading(true);
    try {
      await loadChimesData();

      toaster.push(
        <Message type="success" closable>
          Данные боя часов успешно обновлены
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );

    } catch (error) {
      console.error('Ошибка при обновлении данных боя часов:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка при обновлении данных боя часов
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const getChristmasText = (christmas: string) => {
    return christmas ? "Включен" : "Выключен";
  };

  const formatTime = (hours: string | undefined) => {
    if (hours === undefined || hours === null) return "00:00";
    return `${String(hours).padStart(2, '0')}:00`;
  };

  const loadChimesData = async () => {
    setLoading(true);

    try {
      const response = await ClockChimeSettingsApi.getClockChimeParams(deviceId);

      setChimesData(response);

    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка получения статуса NTP"),
        { placement: "topEnd" }
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deviceId) {
      loadChimesData().then();
    }
  }, [deviceId]);

  if (loading && !refreshLoading) {
    return (
      <div className="fieldset-wrapper">
        <div className="legend-title">Бой часов</div>
        <section className="chime-settings mt-3 mb-3">
          <Loader center content="Загрузка настроек боя..."/>
        </section>
      </div>
    );
  }

  if (error && !refreshLoading) {
    return (
      <div className="fieldset-wrapper">
        <div className="legend-title">Бой часов</div>
        <section className="chime-settings">
          <Message type="error">
            Ошибка загрузки: {error}
          </Message>
          <button onClick={loadChimesData} className="btn btn-primary mt-2">
            Повторить загрузку
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="fieldset-wrapper mt-3">
      <div className="legend-title main-legend-title">Бой часов</div>
      <section className="chime-settings">
        <div className="row p-1">
          <div className="col-md-12 text-end">
            <UpdateButton
              loading={refreshLoading}
              onRefresh={handleRefreshSection}
            />
          </div>
        </div>
        <div className="row" style={{width: '100%'}}>
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 mb-4">
            <div className="fieldset-wrapper control bg-white">
              <div className="legend-title">Время боя</div>
              <div className="shime-time-title text-center">
                1 интервал
              </div>
              <div className="row" style={{justifyContent: 'center'}}>
                <div className="col-md-6 col-6 text-center">
                  <input
                    readOnly={true}
                    className="settings-form-control relay-time-control time-input form-control"
                    value={formatTime(chimesData?.firstStartChimes)}
                    placeholder="00:00"
                  />
                </div>
                <div className="col-md-6 col-6 text-center">
                  <input
                    readOnly={true}
                    className="settings-form-control relay-time-control time-input form-control"
                    value={formatTime(chimesData?.firstEndChimes)}
                    placeholder="00:00"
                  />
                </div>
              </div>
              <div className="shime-time-title text-center mt-3">
                2 интервал
              </div>
              <div className="row" style={{justifyContent: 'center'}}>
                <div className="col-md-6 col-6 text-center">
                  <input
                    readOnly={true}
                    className="settings-form-control relay-time-control time-input form-control"
                    value={formatTime(chimesData?.secondStartChimes)}
                    placeholder="00:00"
                  />
                </div>
                <div className="col-md-6 col-6 text-center">
                  <input
                    readOnly={true}
                    className="settings-form-control relay-time-control time-input form-control"
                    value={formatTime(chimesData?.secondEndChimes)}
                    placeholder="00:00"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 mb-4">
            <div className="fieldset-wrapper bg-white" style={{height: '38%', padding: '20px'}}>
              <div className="legend-title">Варианты боя</div>
              <p className="clock-strike-text">
                {getChimesSchemeText(chimesData?.chimesScheme)}
              </p>
            </div>
            <div className="fieldset-wrapper bg-white" style={{height: '38%', padding: ' 20px'}}>
              <div className="legend-title">Режим боя</div>
              <p className="clock-strike-text">
                {getChimesModeText(chimesData?.chimesMode)}
              </p>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 mb-4 text-center">
            <div className="fieldset-wrapper bg-white" style={{height: '38%', padding: '20px'}}>
              <div className="legend-title">Громкость</div>
              <Progress.Line
                percent={chimesData?.chimesSoundLevel ? Math.round((chimesData.chimesSoundLevel / 30) * 100) : 0}
                strokeColor="red"
              />
            </div>
            <div className="fieldset-wrapper bg-white" style={{height: '38%', padding: '20px'}}>
              <div className="legend-title">Новый год</div>
              <Toggle
                size={'md'}
                disabled={true}
                checked={!!chimesData?.christmasChimes}
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF">
                {getChristmasText(chimesData?.christmasChimes)}
              </Toggle>
            </div>
          </div>
          <div className="row">
            <div className="col-6 col-md-4">
              <div className="d-flex align-items-center">
                <div className="me-2">
                  <div className="relay-indicator">
                    <StatusControl status={relayStatus?.amplifierRelayEnabled ? "online" : "offline"}/>
                  </div>
                </div>
                <div className="relay-mode-value">
                  Питание усилителя
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4">
              <div className="d-flex align-items-center">
                <div className="me-2">
                  <div className="relay-indicator">
                    <StatusControl status={relayStatus?.speakerRelayEnabled ? "online" : "offline"}/>
                  </div>
                </div>
                <div className="relay-mode-value">
                  Питание колонок
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="settings" style={{padding: '5px'}}>
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleSettingsClick();
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}>
            <GearIcon className="gear-icon"></GearIcon>
            </Whisper>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ChimeSettingsSection;
